import React, {useContext, useEffect, useState} from 'react';
import {Card, Col, Modal, QRCode, Row} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage} from "react-intl";
import {Navigate} from "react-router-dom";
import '../../assets/css/PatientCreateModal.css';
import {isMobileOnly} from "react-device-detect";
import {PatientSelfRegisterUrlResponse} from "../../api-client";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import {getSelfRegisterApi} from "../../api/apiInstances";
import {LOCALES} from "../../i18n/locales";
import logger from "../../utils/logger";

interface PatientCreateModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Function;
}

// Mode constants for patient creation
const INITIAL_MODE = 0;        // Initial state (show options)
const SELF_REGISTER_MODE = 1;  // Self-registration by patient (show QR code)
const CREATE_MODE = 2;         // Create patient (redirect to form)

const PatientCreateModal = ({isModalOpen, setIsModalOpen}: PatientCreateModalProps) => {

    const {locale} = useContext(DentobookApplicationContext);

    const [mode, setMode] = useState<number>(0);
    const [createUrl, setCreateUrl] = useState<PatientSelfRegisterUrlResponse | undefined>(undefined);

    const selfRegisterApi = getSelfRegisterApi();
    const baseUrl = window.location.origin;

    // Use operator's current locale for QR code language parameter
    // If locale is BG or TR (supported languages with translations), use it
    // Otherwise fallback to EN (default)
    const qrCodeLang = (locale === LOCALES.BULGARIAN || locale === LOCALES.TURKISH)
        ? locale
        : LOCALES.ENGLISH;

    useEffect(() => {
        setMode(INITIAL_MODE);
    }, [isModalOpen]);

    const handleCancel = () => {
        setIsModalOpen(false);
    }

    if (mode === CREATE_MODE) {
        return <Navigate to={"/patients/create"}/>
    }

    const fetchCreationUrlId = () => {
        selfRegisterApi.fetchPatientCreateUrlId()
            .then(res => {
                setMode(SELF_REGISTER_MODE);

                setCreateUrl(res.data);
            })
            .catch(err => logger.log(err));
    }

    const formDesktop = (
        <Modal
            transitionName=""
            className="dentobook-modal"
            title={<FormattedMessage id="add_patient"/>}
            width={690}
            footer={null}
            open={isModalOpen} onCancel={handleCancel}>
            <div>
                {mode === SELF_REGISTER_MODE ?
                    <div className="text-center mt-12 mb-112" style={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'column',
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <p className="font-16 font-weight-500 mb-24"><FormattedMessage id="qr_description"/></p>
                        <QRCode
                            value={`${baseUrl}/patients/self-register/${createUrl?.id}?lang=${qrCodeLang}`}
                            bordered={false}
                        />
                    </div>
                    :
                    <Row gutter={24} className="mb-112 pl-16" style={{marginTop: -32}}>
                        <Col md={12}>
                            <Card bordered={false}
                                  className="dentobook-qr-manual-card"
                                  onClick={fetchCreationUrlId}
                            >
                            <span className="font-20 font-weight-500 text-center"><FormattedMessage
                                id="qr_code_generator"/></span>
                                <div className="flex-group">
                                    <img alt="qr-create" src={require('../../assets/common/qr-create.svg').default}/>
                                    <br/>
                                    <span className="font-weight-400 text-align-center">
                                <FormattedMessage id="qr_code_generator_description"/>
                            </span>
                                </div>
                            </Card>
                        </Col>
                        <Col md={12}>
                            <Card bordered={false}
                                  className="dentobook-qr-manual-card"
                                  onClick={() => setMode(CREATE_MODE)}
                            >
                                <div className="flex-group">
                                <span className="font-20 font-weight-500 text-center"><FormattedMessage
                                    id="manual_input"/></span>
                                    <img alt="manually-create" className="text-center"
                                         src={require('../../assets/common/manually-create.svg').default}/>
                                    <br/>
                                    <span className="font-weight-400 text-align-center">
                                    <FormattedMessage id="manual_input_description"/>
                                </span>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                }
            </div>
        </Modal>
    );

    const formMobile = (
        <Modal
            transitionName=""
            className="dentobook-modal"
            title={<FormattedMessage id="add_patient"/>}
            width={690}
            footer={null}
            styles={{body: {overflowY: 'auto', maxHeight: 'calc(100vh - 200px)'}}}
            open={isModalOpen} onCancel={handleCancel}>
            <div>
                {mode === SELF_REGISTER_MODE ?
                    <div style={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: 'column',
                        textAlign: 'center',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <p className="font-16 font-weight-500 mt-112"><FormattedMessage id="qr_description"/></p>
                        <QRCode
                            value={`${baseUrl}/patients/self-register/${createUrl?.id}?lang=${qrCodeLang}`}
                            bordered={false}
                        />
                    </div>
                    :
                    <Row gutter={24} className="pl-16" style={{marginTop: -72}}>
                        <Col md={12}>
                            <Card bordered={false}
                                  className="dentobook-qr-manual-card"
                                  onClick={fetchCreationUrlId}
                            >
                            <span className="font-20 font-weight-500 text-center"><FormattedMessage
                                id="qr_code_generator"/></span>
                                <div className="flex-group">
                                    <img alt="qr-create" src={require('../../assets/common/qr-create.svg').default}/>
                                    <br/>
                                    <span className="font-weight-400 text-align-center">
                                <FormattedMessage id="qr_code_generator_description"/>
                            </span>
                                </div>
                            </Card>
                        </Col>
                        <Col md={12}>
                            <Card bordered={false}
                                  className="dentobook-qr-manual-card"
                                  onClick={() => setMode(CREATE_MODE)}
                            >
                                <div className="flex-group">
                                <span className="font-20 font-weight-500 text-center"><FormattedMessage
                                    id="manual_input"/></span>
                                    <img alt="manually-create" className="text-center"
                                         src={require('../../assets/common/manually-create.svg').default}/>
                                    <br/>
                                    <span className="font-weight-400 text-align-center">
                                    <FormattedMessage id="manual_input_description"/>
                                </span>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                }
            </div>
        </Modal>
    );

    return isMobileOnly ? formMobile : formDesktop;
};

export default PatientCreateModal;